/**
 * TRUS-791 — delete a form instance from the member's Forms hub.
 *
 * A draft is removed outright after a confirm. A submitted record is withdrawn:
 * it leaves every list but the row is kept, and the API requires a reason, which
 * is recorded with who removed it. The reason box is labelled for what it is
 * ("Reason for deleting this note") rather than a bare "Notes".
 */
import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { formsAPI, type FormInstance } from '@/services/api/forms';

/** A 4xx `detail` is written for staff (no permission, reason missing); show it as-is. */
function deleteErrorMessage(err: unknown, generic: string, offline: string): string {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } }; request?: unknown };
  if (!e?.response) return e?.request ? offline : generic;
  const status = e.response.status ?? 0;
  const detail = e.response.data?.detail;
  if (status >= 400 && status < 500 && typeof detail === 'string' && detail.trim()) return detail.trim();
  return generic;
}

interface DeleteFormInstanceDialogProps {
  instance: Pick<FormInstance, 'id' | 'form_template_name' | 'form_type' | 'status'>;
  /** Submitted (non-draft) records need a reason; drafts do not. */
  reasonRequired: boolean;
  onCancel: () => void;
  onDeleted: (instanceId: number) => void;
}

export function DeleteFormInstanceDialog({ instance, reasonRequired, onCancel, onDeleted }: DeleteFormInstanceDialogProps) {
  const { t } = useAppTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNote = (instance.form_type || '').toLowerCase() === 'progress_note';
  const missingReason = reasonRequired && !reason.trim();

  const title = reasonRequired
    ? isNote
      ? t('formsHub.delete.title_submitted_note', 'Delete this submitted note?')
      : t('formsHub.delete.title_submitted_form', 'Delete this submitted form?')
    : isNote
      ? t('formsHub.delete.title_draft_note', 'Delete this draft note?')
      : t('formsHub.delete.title_draft_form', 'Delete this draft?');

  const description = reasonRequired
    ? t(
        'formsHub.delete.desc_submitted',
        '"{{name}}" will be removed from the member\'s record and from reports. It is kept in the audit history with your name and the reason below.',
        { name: instance.form_template_name },
      )
    : t('formsHub.delete.desc_draft', '"{{name}}" will be deleted. This cannot be undone.', {
        name: instance.form_template_name,
      });

  const reasonLabel = isNote
    ? t('formsHub.delete.reason_label_note', 'Reason for deleting this note')
    : t('formsHub.delete.reason_label_form', 'Reason for deleting this form');

  const confirm = async () => {
    if (missingReason || busy) return;
    setBusy(true);
    setError(null);
    try {
      await formsAPI.deleteFormInstance(instance.id, reasonRequired ? reason : undefined);
      onDeleted(instance.id);
    } catch (err) {
      console.error('Delete form failed:', err);
      setError(
        deleteErrorMessage(
          err,
          t('formsHub.delete.failed', 'Could not delete this form. Please try again; if it keeps failing, contact support.'),
          t('formsHub.delete.offline', 'Could not reach the server. Check your connection and try again.'),
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="delete-form-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {reasonRequired && (
          <div className="space-y-1.5">
            <label htmlFor={`delete-reason-${instance.id}`} className="text-sm font-medium text-ink">
              {reasonLabel} <span className="text-danger-ink">*</span>
            </label>
            <Textarea
              id={`delete-reason-${instance.id}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder={t('formsHub.delete.reason_placeholder', 'e.g. Duplicate of the 10/29 note, entered by mistake')}
            />
          </div>
        )}
        {error && (
          <p className="text-sm text-danger-ink" role="alert">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
            {t('formsHub.delete.cancel', 'Cancel')}
          </Button>
          <Button type="button" variant="danger" disabled={missingReason || busy} onClick={() => void confirm()}>
            <Trash2 className="w-3.5 h-3.5" />
            {busy ? t('formsHub.delete.deleting', 'Deleting…') : t('formsHub.delete.confirm', 'Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
